import { dayKeyInZone } from '@/lib/dayKey';
import type { JourneyFixture, JourneyRun, PlayerJourney } from './playerJourney';
export type JourneyDayFixture = JourneyFixture & {
  result?: JourneyRun['result'];
  displayStatus: JourneyRun['result'] | 'live' | 'next' | 'scheduled';
};
export type PlayerJourneyDay = {
  day: string | null;
  kind: 'dated' | 'played' | 'unscheduled';
  fixtures: JourneyDayFixture[];
};

/** Actual matches only: conditional advancement belongs exclusively to the mini draw. */
export function groupPlayerJourneyDays(
  journey: PlayerJourney,
  zone: string,
  today?: string | null,
): PlayerJourneyDay[] {
  const actual = [
    ...new Map(
      [...journey.run, ...journey.upcoming].map((fixture) => [fixture.id, fixture]),
    ).values(),
  ];
  // A recorded result wins over a duplicated stale scheduled projection.
  for (const settled of journey.run) {
    const index = actual.findIndex((fixture) => fixture.id === settled.id);
    if (index >= 0) actual[index] = settled;
  }
  actual.sort((a, b) => (a.scheduledAt ?? 'z').localeCompare(b.scheduledAt ?? 'z'));
  let hasNext = false;
  const groups = new Map<string, PlayerJourneyDay>();
  for (const fixture of actual) {
    const result = 'result' in fixture ? (fixture.result as JourneyRun['result']) : undefined;
    const live = ['in_progress', 'live'].includes(fixture.status);
    const displayStatus = result ?? (live ? 'live' : hasNext ? 'scheduled' : 'next');
    if (!result && !live) hasNext = true;
    const dated = fixture.scheduledAt && Number.isFinite(Date.parse(fixture.scheduledAt));
    const day = dated ? dayKeyInZone(fixture.scheduledAt!, zone) : null;
    const kind = day ? 'dated' : result ? 'played' : 'unscheduled';
    const key = day ?? kind;
    if (!groups.has(key)) groups.set(key, { day, kind, fixtures: [] });
    groups.get(key)!.fixtures.push({ ...fixture, result, displayStatus });
  }
  if (today && !groups.has(today)) groups.set(today, { day: today, kind: 'dated', fixtures: [] });
  const dated = [...groups.values()]
    .filter((group) => group.day)
    .sort((a, b) => a.day!.localeCompare(b.day!));
  const played = groups.get('played');
  if (played) {
    const index = today ? dated.findIndex((group) => group.day! >= today) : 0;
    dated.splice(index < 0 ? dated.length : index, 0, played);
  }
  const pending = groups.get('unscheduled');
  if (pending) dated.push(pending);
  return dated;
}
